import {Component, EventEmitter, OnInit, Output} from '@angular/core';
import {CompanyMember} from '../../models/company/company-member.model';
import {AuthenticationService} from '../../../core/services';
import {CompanyMemberService, FollowCompanyService} from '../../../core/data-services/company';
import {BasicUser} from '../../models';
import {CONFIG} from '../../../config';

@Component({
  selector: 'rms-user-focus',
  templateUrl: './user-focus.component.html',
  styleUrls: ['./user-focus.component.scss'],
  standalone: false
})
export class UserFocusComponent implements OnInit {

  @Output() updateActiveListEvent = new EventEmitter<Map<string, Array<any>>>();
  @Output() userFocusToggleEvent = new EventEmitter<null>();

  public originalListMap: Map<string, Array<any>> = new Map<string, Array<any>>();

  public tooltipOpenDelay = CONFIG.tooltipOpenDelay;
  public readonly userFocusTooltipText = 'Focus Mode: Click to filter by companies where you or another user are Primary, Secondary, or Following.';

  public userFocus: string = null;
  public userFocusList: Array<string> = new Array<string>();
  public userFocusToggle: boolean;
  public userFocusDropdownOpen = false;

  public companyMemberMap: Map<string, Map<number, string>> = new Map<string, Map<number, string>>();
  public companyFollowerMap: Map<string, Set<number>> = new Map<string, Set<number>>();
  public userFocusFilterMap = new Map<string, boolean>([['primary', true], ['secondary', true], ['follower', true]]);

  public allCompanyIds: Set<number> = new Set<number>();

  public currentUser: BasicUser = new BasicUser();

  constructor(
    private authenticationService: AuthenticationService,
    private companyMemberService: CompanyMemberService,
    private followCompanyService: FollowCompanyService
  ) {
  }

  ngOnInit() {
    this.currentUser = new BasicUser(this.authenticationService.getLoggedUser());
    this.currentUser.fullName = this.currentUser.firstName + ' ' + this.currentUser.lastName;
    this.initializeFocusMaps();
  }

  public initializeFocusMaps() {
    this.companyMemberMap = new Map<string, Map<number, string>>();
    this.companyFollowerMap = new Map<string, Set<number>>();
    this.companyMemberService.getCompanyMembers().subscribe(memberPage => {
      const idSet = this.getCompanyMembers(memberPage.records);
      this.followCompanyService.getAllFollowed({userids: Array.from(idSet)}).subscribe(followerList => {
        this.getCompanyFollowers(followerList);
        if (!this.userFocus) {
          this.userFocus = this.currentUser.fullName;
        }
        if (this.userFocusToggle) {
          this.userFocusToggleEvent.emit();
        }
      });
    });
  }

  public toggleUserFocus() {
    if (!this.userFocusToggle) {
      this.filterPositionLists();
    } else {
      this.restorePositionLists();
    }
    this.userFocusToggle = !this.userFocusToggle;
    this.userFocusToggleEvent.emit();
  }

  public onUserFocusChange() {
    if (this.userFocusToggle) {
      this.restorePositionLists();
      this.filterPositionLists();
      this.userFocusToggleEvent.emit();
    }
  }

  public filterPositionLists() {
    const updateListMap: Map<string, Array<any>> = new Map<string, Array<any>>();
    this.originalListMap.forEach((value, key) => {
      updateListMap.set(key, value.filter(model => {
        let companyId: any;
        if (model.company && model.company.id) {
          companyId = model.company.id;
        } else if (model.companyId) {
          companyId = model.companyId;
        } else {
          companyId = model.id;
        }
        return this.inFocus(Number(companyId));
      }));
    });
    this.updateActiveListEvent.emit(updateListMap);
  }

  public getIndicatorClass(comanyId: number | string) {
    comanyId = Number(comanyId);
    if (this.companyMemberMap.has(this.userFocus) &&
      this.companyMemberMap.get(this.userFocus).has(comanyId) &&
      this.userFocusFilterMap.get(this.companyMemberMap.get(this.userFocus).get(comanyId))) {
      return `${this.companyMemberMap.get(this.userFocus).get(comanyId)}-indicator`;
    }
    if (this.companyFollowerMap.has(this.userFocus) &&
      this.companyFollowerMap.get(this.userFocus).has(comanyId) &&
      this.userFocusFilterMap.get('follower')) {
      return 'follower-indicator';
    }
  }

  public getUserCompanyIds() {
    const companyIdSet = new Set<number>();
    if (this.userFocus && this.userFocusToggle) {
      if (this.companyMemberMap.has(this.userFocus)) {
        this.companyMemberMap.get(this.userFocus).forEach((value, key) => {
          if (this.userFocusFilterMap.get(value)) {
            companyIdSet.add(key);
          }
        });
      }
      if (this.companyFollowerMap.has(this.userFocus)) {
        if (this.userFocusFilterMap.get('follower')) {
          Array.from(this.companyFollowerMap.get(this.userFocus).keys()).forEach(companyId => companyIdSet.add(companyId));
        }
      }
    }
    return companyIdSet;
  }

  private restorePositionLists() {
    this.updateActiveListEvent.emit(this.originalListMap);
  }

  private inFocus(companyId: number) {
    return (this.companyMemberMap.has(this.userFocus) &&
        this.companyMemberMap.get(this.userFocus).has(companyId) &&
        this.userFocusFilterMap.get(this.companyMemberMap.get(this.userFocus).get(companyId))) ||
      (this.companyFollowerMap.has(this.userFocus) &&
        this.companyFollowerMap.get(this.userFocus).has(companyId) &&
        this.userFocusFilterMap.get('follower'));
  }

  private getCompanyMembers(data: Array<CompanyMember>): Set<number> {
    const idSet = new Set<number>();
    if (this.currentUser.id) {
      idSet.add(this.currentUser.id);
    }
    data.forEach(companyMember => {
      if (companyMember.primaryUserId) {
        idSet.add(companyMember.primaryUserId);
      }
      if (companyMember.secondaryUserId) {
        idSet.add(companyMember.secondaryUserId);
      }
      this.setCompanyMemberMap(companyMember.secondaryUserActive, companyMember.secondaryUserFullname, companyMember.companyId, 'secondary');
      this.setCompanyMemberMap(companyMember.primaryUserActive, companyMember.primaryUserFullname, companyMember.companyId, 'primary');
      this.allCompanyIds.add(companyMember.companyId);
    });
    const nameSet = new Set<string>();
    nameSet.add(this.currentUser.fullName);
    Array.from(this.companyMemberMap.keys()).forEach(name => {
      nameSet.add(name);
    });
    this.userFocusList = Array.from(nameSet).sort();
    return idSet;
  }

  private setCompanyMemberMap(active: boolean, fullName: string, companyId: number, tag: string) {
    if (active) {
      if (!this.companyMemberMap.has(fullName)) {
        this.companyMemberMap.set(fullName, new Map<number, string>());
      }
      this.companyMemberMap.get(fullName).set(companyId, tag);
    }
  }

  private getCompanyFollowers(data: Array<any>) {
    data.forEach(companyFollower => {
      if (!this.companyFollowerMap.has(companyFollower.user.fullName)) {
        this.companyFollowerMap.set(companyFollower.user.fullName, new Set<number>());
      }
      this.companyFollowerMap.get(companyFollower.user.fullName).add(companyFollower.companyId);
      this.allCompanyIds.add(companyFollower.companyId);
    });
  }
}
